import React, {type ReactNode, useState} from 'react'
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle, TransitionChild} from '@headlessui/react'
import Button from '@/Components/Buttons/Button'

/**
 * "Looking to pay?" — shown when a coach clicks a tournament this team has
 * already registered for and still owes (ENG-4346). The card and Yes/No row
 * match this page's verification offer (`VerificationPrompt` in
 * RegistrationForm.tsx). The backdrop matches the other Headless dialogs.
 * Yes continues to that tournament's payment page. No, Escape, and the
 * backdrop all dismiss without selecting the tournament.
 *
 * Nothing can close it until the backdrop's fade-in finishes: Headless UI
 * 2.2.10 can strand a close that interrupts the fade, leaving an invisible
 * overlay that swallows every click (ENG-4433).
 */
export default function LookingToPayDialog(
    {tournamentName, open, opening, onConfirm, onDismiss}: {
        tournamentName: string
        open: boolean
        opening: boolean
        onConfirm: () => void
        onDismiss: () => void
    },
): ReactNode {
    const [entered, setEntered] = useState(false)
    const locked = opening || !entered

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!locked) {
                    onDismiss()
                }
            }}
            className={'relative z-50'}
        >
            <TransitionChild
                afterEnter={() => setEntered(true)}
                beforeLeave={() => setEntered(false)}
            >
                <DialogBackdrop
                    className={'fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in'}
                />
            </TransitionChild>
            <div className={'fixed inset-0 z-10 flex w-screen items-center justify-center overflow-y-auto p-4'}>
                <DialogPanel
                    data-testid={'registration-looking-to-pay'}
                    className={'flex w-full max-w-2xl flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6'}
                >
                    <DialogTitle className={'rounded text-base font-semibold text-gray-800 focus-visible:ring-2 focus-visible:ring-royal-20 focus-visible:ring-offset-2 focus-visible:outline-none'}>
                        Looking to pay?
                    </DialogTitle>
                    <p className={'break-words text-[14px] text-gray-700'}>
                        {`You're already registered for ${tournamentName}. Continue to payment for this tournament.`}
                    </p>
                    <div className={'flex gap-3'}>
                        <Button
                            mutedDisabled
                            disabled={locked}
                            onClick={onConfirm}
                            testId={'registration-looking-to-pay-confirm'}
                            classes={['flex-1 cursor-pointer']}
                        >
                            {opening ? 'Opening…' : 'Yes'}
                        </Button>
                        <Button
                            outlined
                            mutedDisabled
                            disabled={locked}
                            onClick={onDismiss}
                            testId={'registration-looking-to-pay-dismiss'}
                            classes={['flex-1 cursor-pointer']}
                        >
                            No
                        </Button>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    )
}
